import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import Cookies from "js-cookie";

// Cookies are not reliably JSON — services/api.js already try/catches the
// same parse for `token`. This object is built at import time, before React
// mounts, so an unguarded throw here is NOT catchable by ErrorBoundary and
// white-screens the whole app.
function readJsonCookie(name) {
  const raw = Cookies.get(name);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return raw;
  }
}

const storedTheme = readJsonCookie("theme");

const initialValue = {
  userinfo: readJsonCookie("user"),
  platformUser: readJsonCookie("platform-user"),
  theme:
    storedTheme !== null && storedTheme !== undefined
      ? storedTheme
      : window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
  fcm: readJsonCookie("fcm"),
};
export const userSlice = createSlice({
  name: "user",
  initialState: initialValue,

  reducers: {
    login: (state, action) => {
      state.userinfo = action.payload.data.user;
      Cookies.set("user", JSON.stringify(action.payload.data.user), {
        expires: 90,
      });
      Cookies.set("token", JSON.stringify(action.payload.data.token), {
        expires: 90,
      });
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${action.payload.data.token}`;
    },

    updateProfilePhoto: (state, action) => {
      state.userinfo.photo = action.payload;
      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    updateCoverPhoto: (state, action) => {
      state.userinfo.cover = action.payload;
      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    updateUserInfo: (state, action) => {
      state.userinfo = {
        ...(state.userinfo || {}),
        ...(action.payload || {}),
      };
      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    updateNStats: (state, action) => {
      state.userinfo.recivedRequestsCount =
        action.payload.recivedRequestsCount >= 0
          ? action.payload.recivedRequestsCount
          : state.userinfo.recivedRequestsCount;

      state.userinfo.unseenMessages =
        action.payload.unseenMessages >= 0
          ? action.payload.unseenMessages
          : state.userinfo.unseenMessages;

      state.userinfo.unseenNotification =
        action.payload.unseenNotification >= 0
          ? action.payload.unseenNotification
          : state.userinfo.unseenNotification;

      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    decrementUnseen: (state, action) => {
      const type = action.payload?.type;
      if (type === "notification" && state.userinfo.unseenNotification > 0) {
        state.userinfo.unseenNotification -= 1;
      } else if (type === "message" && state.userinfo.unseenMessages > 0) {
        state.userinfo.unseenMessages -= 1;
      }
      Cookies.set("user", JSON.stringify(state.userinfo), { expires: 90 });
    },

    reciveNoti: (state, action) => {
      if (action.payload?.type === "notification") {
        state.userinfo.unseenNotification =
          state.userinfo.unseenNotification + 1;
      } else if (action?.payload.type === "message") {
        state.userinfo.unseenMessages = state.userinfo.unseenMessages + 1;
      }

      Cookies.set("user", JSON.stringify(state.userinfo), {
        expires: 90,
      });
    },

    logout: (state, action) => {
      state.userinfo = null;
      Cookies.remove("user");
      Cookies.remove("token");
      delete axios.defaults.headers.common["Authorization"];
    },

    platformLogin: (state, action) => {
      state.platformUser = action.payload.data.user;
      Cookies.set("platform-user", JSON.stringify(action.payload.data.user), { expires: 90 });
      Cookies.set("platform-token", JSON.stringify(action.payload.data.token), { expires: 90 });
    },

    platformLogout: (state) => {
      state.platformUser = null;
      // `Cookies.set(name, null)` stores the literal string "null", which
      // then reads back as a truthy cookie — `platform-token` is used raw as
      // a Bearer token (MediaUploadField.jsx, pages/platform/shared.jsx).
      // Remove them, exactly as the `logout` reducer above does.
      Cookies.remove("platform-user");
      Cookies.remove("platform-token");
    },

    // Re-hydrate platform auth from the server whoami (httpOnly jwt-platform
    // session) on a fresh page load. Auth no longer depends on the client
    // `platform-user` cookie being readable — some browsers don't surface it on
    // a deep-link load — but we refresh it here so subsequent loads are instant.
    setPlatformUser: (state, action) => {
      const user = action.payload || null;
      state.platformUser = user;
      if (user) {
        Cookies.set("platform-user", JSON.stringify(user), { expires: 90 });
      }
    },

    changeTheme: (state, action) => {
      state.theme = action.payload;
      Cookies.set("theme", JSON.stringify(action.payload));
    },
  },
});
export const {
  login,
  logout,
  platformLogin,
  platformLogout,
  setPlatformUser,
  updateProfilePhoto,
  updateCoverPhoto,
  updateUserInfo,
  changeTheme,
  updateNStats,
  reciveNoti,
  decrementUnseen,
} = userSlice.actions;

export default userSlice.reducer;
